import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faLink } from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import DecayTriggerConfigManagement from "./DecayTriggerConfigManagement";
import InlineFieldTooltip from "../components/InlineFieldTooltip";

const NewSimulationTriggersManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab =
    (searchParams.get("tab") || "").toLowerCase() === "fallback"
      ? "fallback"
      : "simulation";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    trigger_touchup_pct: "",
    trigger_overcoat_pct: "",
    trigger_recoat_pct: "",
    is_active: true,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  useEffect(() => {
    fetchConfigs();
  }, []);

  useEffect(() => {
    const tabParam = (searchParams.get("tab") || "").toLowerCase();
    if (tabParam === "fallback" && activeTab !== "fallback") {
      setActiveTab("fallback");
    }
    if (tabParam !== "fallback" && activeTab !== "simulation") {
      setActiveTab("simulation");
    }
  }, [searchParams, activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    if (tab === "fallback") {
      params.set("tab", "fallback");
    } else {
      params.delete("tab");
    }
    setSearchParams(params, { replace: true });
  };

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/new-simulation-triggers");
      if (response.data.success) {
        setConfigs(response.data.data || []);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to fetch trigger configurations. Ensure the new_simulation_trigger_config table exists.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingConfig) return;
    try {
      setSaving(true);
      await api.put(`/new-simulation-triggers/${editingConfig.id}`, {
        trigger_touchup_pct:
          formData.trigger_touchup_pct !== ""
            ? parseFloat(formData.trigger_touchup_pct)
            : undefined,
        trigger_overcoat_pct:
          formData.trigger_overcoat_pct !== ""
            ? parseFloat(formData.trigger_overcoat_pct)
            : undefined,
        trigger_recoat_pct:
          formData.trigger_recoat_pct !== ""
            ? parseFloat(formData.trigger_recoat_pct)
            : undefined,
        is_active: formData.is_active,
      });
      await fetchConfigs();
      setShowModal(false);
      resetForm();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      trigger_touchup_pct:
        config.trigger_touchup_pct != null
          ? String(config.trigger_touchup_pct)
          : "",
      trigger_overcoat_pct:
        config.trigger_overcoat_pct != null
          ? String(config.trigger_overcoat_pct)
          : "",
      trigger_recoat_pct:
        config.trigger_recoat_pct != null
          ? String(config.trigger_recoat_pct)
          : "",
      is_active: config.is_active !== false,
    });
    setShowModal(true);
  };

  const handleToggleActive = async (config) => {
    try {
      await api.put(`/new-simulation-triggers/${config.id}`, {
        is_active: !config.is_active,
      });
      await fetchConfigs();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update status");
    }
  };

  const resetForm = () => {
    setFormData({
      trigger_touchup_pct: "",
      trigger_overcoat_pct: "",
      trigger_recoat_pct: "",
      is_active: true,
    });
    setEditingConfig(null);
  };

  const filteredConfigs = configs.filter((config) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (config.combination_code &&
        config.combination_code.toLowerCase().includes(s)) ||
      (config.corrosion_category_code &&
        config.corrosion_category_code.toLowerCase().includes(s)) ||
      (config.damage_severity_code &&
        config.damage_severity_code.toLowerCase().includes(s)) ||
      (config.position && config.position.toLowerCase().includes(s))
    );
  });
  const totalFiltered = filteredConfigs.length;
  const totalPages = Math.ceil(totalFiltered / pageSize) || 1;
  const paginatedConfigs = filteredConfigs.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-gray-600">Loading trigger configurations...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex items-start gap-2">
        <p className="text-gray-600">
          Configure thresholds used by the New Simulation Engine. Use the
          fallback tab only for baseline category thresholds when no
          simulation-trigger row matches.
        </p>
        <InlineFieldTooltip
          page="simulation_triggers"
          section="page_description"
          fieldKey="simulation_triggers_overview"
        />
      </div>

      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Tabs">
          <button
            type="button"
            onClick={() => handleTabChange("simulation")}
            className={`pb-3 text-sm font-medium border-b-2 ${
              activeTab === "simulation"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Simulation Triggers
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("fallback")}
            className={`pb-3 text-sm font-medium border-b-2 ${
              activeTab === "fallback"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Decay Trigger Config (Fallback)
          </button>
        </nav>
      </div>

      {activeTab === "fallback" ? (
        <DecayTriggerConfigManagement embedded fallbackMode />
      ) : (
        <>
          <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            These are the default triggers used in simulation calculations. They
            can be edited as well in simulation scenario configuration.
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="mb-6">
            <input
              type="text"
              placeholder="Search by combination code, category, severity, or position..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Combination
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Touch-up %
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Overcoat %
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recoat %
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredConfigs.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      No trigger configurations found. Run the New Simulation
                      Engine SQL to create the table, and add combinations (they
                      will get default trigger rows automatically).
                    </td>
                  </tr>
                ) : (
                  paginatedConfigs.map((config) => (
                    <tr key={config.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FontAwesomeIcon
                            icon={faLink}
                            className="h-5 w-5 text-blue-500 mr-2"
                          />
                          <span className="text-sm font-medium text-gray-900">
                            {config.combination_code}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-900">
                            {config.position || "-"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-sm text-gray-900">
                          {config.trigger_touchup_pct}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-sm text-gray-900">
                          {config.trigger_overcoat_pct}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-sm text-gray-900">
                          {config.trigger_recoat_pct}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={config.is_active}
                          onClick={() => handleToggleActive(config)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            config.is_active ? "bg-blue-600" : "bg-gray-300"
                          }`}
                          title={
                            config.is_active ? "Set inactive" : "Set active"
                          }
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              config.is_active
                                ? "translate-x-6"
                                : "translate-x-1"
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEdit(config)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit thresholds"
                        >
                          <FontAwesomeIcon icon={faPencil} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalFiltered > pageSize && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4 bg-white px-4 py-3 rounded-b-lg">
              <p className="text-sm text-gray-700">
                Showing {(page - 1) * pageSize + 1} to{" "}
                {Math.min(page * pageSize, totalFiltered)} of {totalFiltered}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {showModal && editingConfig && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
              <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-4">
                  <span>
                    Edit triggers - {editingConfig.combination_code}
                    {editingConfig.position
                      ? ` (${editingConfig.position})`
                      : ""}
                  </span>
                  <InlineFieldTooltip
                    page="simulation_triggers"
                    section="modal_titles"
                    fieldKey="trigger_thresholds_form"
                    fallbackText="Edit touch-up, overcoat, and recoat trigger thresholds."
                    title="Trigger Thresholds Form"
                  />
                </h3>
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Touch-up threshold (%)
                    </label>
                    <input
                      type="number"
                      name="trigger_touchup_pct"
                      value={formData.trigger_touchup_pct}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 1.0"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Decay % to trigger Touch-up (TU)
                    </p>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Overcoat threshold (%)
                    </label>
                    <input
                      type="number"
                      name="trigger_overcoat_pct"
                      value={formData.trigger_overcoat_pct}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 1.5"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Decay % to trigger Overcoat (OC)
                    </p>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Recoat threshold (%)
                    </label>
                    <input
                      type="number"
                      name="trigger_recoat_pct"
                      value={formData.trigger_recoat_pct}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 2.0"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Decay % to trigger Recoat/Full (RE)
                    </p>
                  </div>
                  <div className="mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                        className="mr-2 rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-500 disabled:opacity-50"
                    >
                      {saving ? "Saving..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NewSimulationTriggersManagement;
